/**
 * Toast Context - Global toast notification system
 *
 * Provides:
 * - ToastProvider wrapper component
 * - useToast hook for showing toasts
 * - Toast container with bottom center positioning
 */

import { createContext, useCallback, useContext, useState, useMemo } from 'react';

import { Toast } from './Toast';

// Toast item interface
interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
}

// Context value interface
interface ToastContextValue {
  showToast: (message: string, options?: { type?: ToastItem['type']; duration?: number }) => void;
  dismissToast: (id: string) => void;
}

// Create context
const ToastContext = createContext<ToastContextValue | null>(null);

// Generate unique ID
let toastId = 0;
const generateId = () => `toast-${String(++toastId)}`;

// Provider component
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Show a new toast
  const showToast = useCallback(
    (message: string, options?: { type?: ToastItem['type']; duration?: number }) => {
      const id = generateId();
      const newToast: ToastItem = {
        id,
        message,
        type: options?.type ?? 'success',
        duration: options?.duration ?? 3000,
      };

      setToasts([newToast]);
    },
    []
  );

  // Dismiss a toast by ID
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Memoize context value
  const value = useMemo(
    () => ({
      showToast,
      dismissToast,
    }),
    [showToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container - Fixed at bottom center */}
      {toasts.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex flex-col gap-2 items-center"
          aria-label="Notifications"
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onDismiss={dismissToast}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

// Hook to use toast
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
